import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/ui/InputField';
import SelectField from '../../../../components/ui/SelectField';
import { useEventTypeInput } from '../../../../hooks/useEventTypeInput';
import { withoutControlCharacters } from '../../../../utils/eventTypeInput';
import { EVENT_TYPE_MAX_LENGTH } from '../../../services/serviceConfigurationRules';

interface EventTypeFieldProps {
    /** The selected room's Group — decides dropdown vs free text. */
    groupId: string | null | undefined;
    value: string | null | undefined;
    onChange: (value: string) => void;
    /** The dropdown's vocabulary (`useEventTypeOptions`). Unused in free text. */
    options: string[];
    disabled?: boolean;
}

/**
 * The event field of the admin venue booking screens (create, series, edit).
 *
 * A dropdown of event types, or — when the room's category is set to free
 * text (`useEventTypeInput`) — a name staff type, e.g. "Damen 2". Same cap
 * (40) and the same control-character stripping as the end-user app's field;
 * the name is trimmed on blur, so a stray space never lands in the booking.
 */
const EventTypeField = ({ groupId, value, onChange, options, disabled }: Readonly<EventTypeFieldProps>) => {
    const { t } = useTranslation();
    const { mode, failed } = useEventTypeInput(groupId);

    // When the room's setting could not be read, typing is the input that
    // works for every room — said on screen, never passed off as "this room
    // uses the dropdown".
    if (mode === 'freeText' || failed) {
        return (
            <InputField
                value={value ?? ''}
                onChange={(typed) => onChange(withoutControlCharacters(typed))}
                onBlur={() => {
                    // Only when trimming changes something: tabbing through an
                    // empty field must not turn "no event" (null) into ''.
                    const cleaned = withoutControlCharacters(value ?? '').trim();
                    if (cleaned !== (value ?? '')) onChange(cleaned);
                }}
                maxLength={EVENT_TYPE_MAX_LENGTH}
                disabled={disabled}
                label={t('create_booking.reservation_details.event_title')}
                error={failed}
                errorText={failed ? t('create_booking.reservation_details.event_input_load_failed') : undefined}
            />
        );
    }

    return (
        <SelectField
            options={options}
            value={value ?? ''}
            onChange={onChange}
            disabled={disabled}
            label={t('create_booking.reservation_details.event_type')}
        />
    );
};

export default EventTypeField;
